// ===== Mention Autocomplete Popup =====
// "@" 입력 시 방 멤버 + mentionable 에이전트를 후보로 필터링하는 팝업.
// public/js/features/command-autocomplete.ts("/" 명령 팝업)의 IME·키보드·ARIA·외부 클릭
// 닫기 골격을 미러 소스로 삼는다(Phase 4 설계). 다만 쿼리 추출은 캐럿 중간의 "@word" 를
// 잡아야 하므로 미러의 getSlashQuery(줄 첫 글자 "/" 전용)는 그대로 못 쓰고 목업의
// currentQuery() 방식(selectionStart 기준 정규식)을 따른다.

type MentionCandidate = { kind: "all" | "human" | "agent"; id: string; name: string; desc: string };

type MentionState =
  | { kind: "loading" }
  | { kind: "failure"; reason: "membership" | "network" }
  | { kind: "ready"; humans: MentionCandidate[]; agents: MentionCandidate[]; everyone: MentionCandidate[] };

(function mentionAutocomplete() {
  const popupElement = document.getElementById("mentionAutocomplete");
  const inputElement = document.getElementById("messageInput");
  if (!(popupElement instanceof HTMLElement) || !(inputElement instanceof HTMLTextAreaElement)) return;
  const popup = popupElement;
  const input = inputElement;

  // ARIA 초기화 (미러 :29-34)
  popup.setAttribute("role", "listbox");
  popup.id = popup.id || "mentionAutocomplete";
  input.setAttribute("aria-autocomplete", "list");
  input.setAttribute("aria-controls", popup.id);
  input.setAttribute("aria-expanded", "false");

  let isOpen = false;
  let flatItems: MentionCandidate[] = [];   // 현재 렌더된 선택 가능 항목(그룹 경계를 가로지른 평탄 목록)
  let selectedIdx = -1;
  let atStart = -1;            // "@" 문자의 절대 위치
  let currentQueryText = "";   // 필터 쿼리 텍스트

  let candidatesState: (MentionState & { kind: "ready" }) | null = null; // 캐시된 마지막 성공 상태
  let candidatesRoomId: string | null | undefined = undefined; // candidatesState가 속한 룸(성공 시에만 갱신)
  let cachedRoomId: string | null | undefined = undefined; // 진행 중/마지막 시도 대상 룸(중복 fetch 방지용)
  let _fetchPromise: Promise<MentionState> | null = null;  // 중복 fetch 방지(미러 :40,47)

  // ===== 현재 룸 SSOT (features/work-badge.ts:224-225 선례와 동일 방식) =====
  function currentRoomId(): string | null {
    return typeof wsClient !== "undefined" && wsClient ? wsClient._currentRoomId : null;
  }

  // 이미 화면에 로드되어 있는 로그인 액터 컨텍스트(auth-client.ts)에서 자기 actorId를 읽는다.
  // 전역 액터 목록 엔드포인트를 새로 호출해 재도출하지 않는다(FP5/GM5 — 방 밖 사람 유입 금지).
  // window.BigtionAuth 타입은 auth-client.ts의 `interface Window` 전역 선언을 그대로 쓴다
  // (같은 tsconfig.frontend.json 프로그램의 non-module 스크립트끼리는 Window 선언 병합이
  // 프로젝트 전체에 적용된다) — 여기서 다시 선언하거나 unknown 을 거쳐 좁힐 필요가 없다.
  function selfActorId(): string | null {
    const raw = window.BigtionAuth?.actorContext?.actor?.actor;
    return typeof raw === "string" ? raw : null;
  }

  // ===== 한글 초성 매칭(D09) =====
  const CHO = ["ㄱ", "ㄲ", "ㄴ", "ㄷ", "ㄸ", "ㄹ", "ㅁ", "ㅂ", "ㅃ", "ㅅ", "ㅆ", "ㅇ", "ㅈ", "ㅉ", "ㅊ", "ㅋ", "ㅌ", "ㅍ", "ㅎ"];
  function chosungOf(str: string): string {
    let out = "";
    for (let i = 0; i < str.length; i++) {
      const code = str.charCodeAt(i) - 0xAC00;
      out += (code >= 0 && code <= 11171) ? CHO[Math.floor(code / 588)] : str[i];
    }
    return out;
  }

  // 이름 부분일치(대소문자 무시) 또는 초성 접두 일치
  function filterMentionCandidates(candidates: MentionCandidate[], query: string): MentionCandidate[] {
    if (!query) return candidates;
    const lowerQuery = query.toLowerCase();
    return candidates.filter((candidate) => {
      if (candidate.name.toLowerCase().includes(lowerQuery)) return true;
      return chosungOf(candidate.name).startsWith(query);
    });
  }

  // ===== 후보 소스 A: mentionable 에이전트 =====
  async function fetchMentionableAgents(): Promise<{ agents: MentionCandidate[]; failure: "network" | null }> {
    try {
      const res = await fetch("/api/agents");
      if (!res.ok) return { agents: [], failure: "network" };
      const payload: unknown = await res.json();
      const record = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
      const list = Array.isArray(record.agents) ? record.agents : [];
      const agents: MentionCandidate[] = list.flatMap((entry): MentionCandidate[] => {
        if (typeof entry !== "object" || entry === null) return [];
        const agentRecord = entry as Record<string, unknown>;
        if (typeof agentRecord.id !== "string" || typeof agentRecord.name !== "string" || agentRecord.mentionable !== true) return [];
        return [{
          kind: "agent",
          id: agentRecord.id,
          name: agentRecord.name,
          desc: typeof agentRecord.role === "string" ? agentRecord.role : "",
        }];
      });
      return { agents, failure: null };
    } catch (err) {
      console.error("[mention-autocomplete] agents fetch failed:", err);
      return { agents: [], failure: "network" };
    }
  }

  // ===== 후보 소스 B: 현재 방 멤버 =====
  // 전역 액터 목록 엔드포인트는 쓰지 않는다 — 방 밖 사람이 새어 들어온다(FP5/GM5).
  async function fetchRoomMembers(roomId: string): Promise<{ humans: MentionCandidate[]; failure: "membership" | "network" | null }> {
    try {
      const res = await fetch(`/api/rooms/${roomId}/members`);
      if (res.status === 403) {
        // findVisibleRoom 실패 { ok:false, error:"room_membership_required" } — 정상 응답 형태라
        // res.ok 만 보면 조용히 빈 목록이 된다. 명시적으로 잡는다(설계 F).
        return { humans: [], failure: "membership" };
      }
      if (!res.ok) return { humans: [], failure: "network" };
      const payload: unknown = await res.json();
      const record = payload && typeof payload === "object" ? payload as Record<string, unknown> : {};
      if (record.ok !== true) return { humans: [], failure: "network" };
      const members = Array.isArray(record.members) ? record.members : [];
      const self = selfActorId();
      const humans: MentionCandidate[] = members.flatMap((entry): MentionCandidate[] => {
        if (typeof entry !== "object" || entry === null) return [];
        const memberRecord = entry as Record<string, unknown>;
        const actorId = typeof memberRecord.actorId === "string" ? memberRecord.actorId : null;
        const displayName = typeof memberRecord.displayName === "string" ? memberRecord.displayName : null;
        if (!actorId) return [];
        // listMembers()가 display_name 없는 행을 actor_id(불투명 핸들)로 폴백시키므로,
        // displayName === actorId 는 실질적으로 "핸들 없음"이다 — 후보에서 제외한다(설계 B).
        if (!displayName || displayName === actorId) return [];
        if (self && actorId === self) return []; // 자기 자신 제외(목업 mockup.html:607)
        return [{ kind: "human", id: actorId, name: displayName, desc: "" }];
      });
      return { humans, failure: null };
    } catch (err) {
      console.error("[mention-autocomplete] members fetch failed:", err);
      return { humans: [], failure: "network" };
    }
  }

  // 후보를 한 번 받아 캐시하고, 룸이 바뀌면 캐시를 버리고 다시 받는다(설계 B, 미러 _fetchPromise 패턴).
  async function loadMentionCandidates(): Promise<MentionState> {
    const roomId = currentRoomId();
    if (candidatesState && candidatesRoomId === roomId) return candidatesState;
    if (_fetchPromise && cachedRoomId === roomId) return _fetchPromise;

    cachedRoomId = roomId;
    const promise = (async (): Promise<MentionState> => {
      const agentsResult = await fetchMentionableAgents();
      let humans: MentionCandidate[] = [];
      let everyone: MentionCandidate[] = [];
      let membersFailure: "membership" | "network" | null = null;
      if (roomId !== null) {
        const membersResult = await fetchRoomMembers(roomId);
        humans = membersResult.humans;
        everyone = membersResult.failure
          ? []
          : [{ kind: "all", id: "all", name: "all", desc: "이 방의 모든 팀원" }];
        membersFailure = membersResult.failure;
      }
      const failure = membersFailure || agentsResult.failure;
      return failure
        ? { kind: "failure", reason: failure }
        : { kind: "ready", humans, agents: agentsResult.agents, everyone };
    })();
    _fetchPromise = promise;

    const state = await promise;
    if (_fetchPromise === promise) _fetchPromise = null;
    if (state.kind === "ready") {
      candidatesState = state;
      candidatesRoomId = roomId;
    }
    return state;
  }

  // ===== 쿼리 추출 (목업 mockup.html:596-604 방식 — 캐럿 앞 "@word") =====
  function currentMentionQuery(): { query: string; atPos: number } | null {
    const pos = input.selectionStart ?? input.value.length;
    const before = input.value.slice(0, pos);
    const match = before.match(/@([^\s@]*)$/);
    if (!match) return null;
    return { query: match[1], atPos: pos - match[0].length };
  }

  // ===== 렌더링 =====
  function repaintSelection(): void {
    const nodes = popup.querySelectorAll(".mention-ac-item");
    nodes.forEach((el, i) => {
      const selected = i === selectedIdx;
      el.classList.toggle("selected", selected);
      el.setAttribute("aria-selected", String(selected));
    });
    if (selectedIdx >= 0) {
      input.setAttribute("aria-activedescendant", `mention-ac-opt-${selectedIdx}`);
    } else {
      input.removeAttribute("aria-activedescendant");
    }
    const selectedEl = popup.querySelector(".mention-ac-item.selected");
    if (selectedEl && typeof selectedEl.scrollIntoView === "function") selectedEl.scrollIntoView({ block: "nearest" });
  }

  function renderMentionPopup(state: MentionState): void {
    let html = "";
    const items: MentionCandidate[] = [];

    if (state.kind === "loading") {
      html = `<div class="mention-ac-state" role="presentation">멘션 후보를 불러오는 중…</div>`;
    } else if (state.kind === "failure") {
      const message = state.reason === "membership"
        ? "이 방의 멤버 목록을 볼 권한이 없어요."
        : "멘션 후보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.";
      html = `<div class="mention-ac-state" role="presentation">${escapeHtml(message)}</div>`;
    } else {
      const humans = filterMentionCandidates(state.humans, currentQueryText);
      const agents = filterMentionCandidates(state.agents, currentQueryText);
      const everyone = filterMentionCandidates(state.everyone, currentQueryText);
      const groups: Array<{ label: string; list: MentionCandidate[] }> = [];
      if (humans.length) groups.push({ label: "팀원", list: humans });
      if (agents.length) groups.push({ label: "에이전트", list: agents });
      // 실수로 전체 호출하지 않도록 기존 개인/에이전트 후보 뒤에 둔다.
      if (everyone.length) groups.push({ label: "전체", list: everyone });

      if (groups.length === 0) {
        // 0건이어도 팝업을 닫지 않고 빈 상태를 보여준다(SC12/CAP13, 설계 C)
        html = `<div class="mention-ac-state" role="presentation">"${escapeHtml(currentQueryText)}"와 일치하는 상대가 없어요</div>`;
      } else {
        for (const group of groups) {
          html += `<div class="mention-ac-group" role="presentation">${escapeHtml(group.label)}</div>`;
          for (const candidate of group.list) {
            const idx = items.length;
            items.push(candidate);
            const descHtml = candidate.desc ? `<span class="mention-ac-desc">${escapeHtml(candidate.desc)}</span>` : "";
            html += `<div class="mention-ac-item" data-idx="${idx}" id="mention-ac-opt-${idx}" role="option" aria-selected="false">` +
              `<span class="mention-ac-avatar">${escapeHtml(candidate.name.charAt(0))}</span>` +
              `<span class="mention-ac-name">${escapeHtml(candidate.name)}</span>` +
              descHtml +
              `</div>`;
          }
        }
      }
    }

    flatItems = items;
    selectedIdx = items.length > 0 ? 0 : -1;
    popup.innerHTML = html;
    popup.style.display = "block";
    isOpen = true;
    input.setAttribute("aria-expanded", "true");
    repaintSelection();
  }

  // ===== 열기/닫기 =====
  async function syncMentionPopup(query: string, atPos: number): Promise<void> {
    atStart = atPos;
    currentQueryText = query;
    isOpen = true;

    if (candidatesState && candidatesRoomId === currentRoomId()) {
      renderMentionPopup(candidatesState);
      return;
    }

    renderMentionPopup({ kind: "loading" });
    const state = await loadMentionCandidates();
    if (!isOpen) return; // 그 사이 팝업이 닫혔으면 렌더하지 않는다
    renderMentionPopup(state);
  }

  function close(): void {
    popup.style.display = "none";
    popup.innerHTML = "";
    isOpen = false;
    selectedIdx = -1;
    flatItems = [];
    atStart = -1;
    input.setAttribute("aria-expanded", "false");
    input.removeAttribute("aria-activedescendant");
  }

  // ===== 선택 확정 =====
  function selectMentionCandidate(idx: number): void {
    const item = flatItems[idx];
    if (!item) return;
    const caretPos = input.selectionStart ?? input.value.length;
    const before = input.value.slice(0, atStart);
    const after = input.value.slice(caretPos);
    input.value = before + "@" + item.name + " " + after;
    const caret = atStart + item.name.length + 2; // "@" 1 + 공백 1
    close();
    input.focus();
    input.setSelectionRange(caret, caret);
  }

  // ===== "@" 감지 + 필터링 =====
  function handleTextChange(): void {
    const match = currentMentionQuery();
    if (match) {
      void syncMentionPopup(match.query, match.atPos);
    } else if (isOpen) {
      close();
    }
  }

  input.addEventListener("input", handleTextChange);
  input.addEventListener("click", handleTextChange);

  // ===== 이벤트: 한글 IME 조합 =====
  // compositionstart에서 잠그는 목업 방식은 채택하지 않는다 — 그러면 초성 검색이
  // compositionend 이전에는 절대 성립할 수 없다(D09, ## 배경).
  input.addEventListener("compositionupdate", () => {
    const match = currentMentionQuery();
    if (match) void syncMentionPopup(match.query, match.atPos);
  });

  input.addEventListener("compositionend", () => {
    // compositionend 후 input 이벤트가 오지 않는 브라우저 대비(미러 :204-207)
    handleTextChange();
  });

  // ===== 이벤트: 키보드 네비게이션 =====
  input.addEventListener("keydown", (e) => {
    if (!isOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (flatItems.length > 0) {
          selectedIdx = (selectedIdx + 1) % flatItems.length;
          repaintSelection();
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (flatItems.length > 0) {
          selectedIdx = (selectedIdx - 1 + flatItems.length) % flatItems.length;
          repaintSelection();
        }
        break;

      case "Tab":
        e.preventDefault();
        if (selectedIdx >= 0) selectMentionCandidate(selectedIdx);
        break;

      case "Enter":
        if (selectedIdx >= 0 && !e.shiftKey) {
          e.preventDefault();
          // 같은 #messageInput에 전송 핸들러가 붙어 있다 — 안 하면 멘션 확정이 곧 메시지 전송이
          // 된다(미러 :231-237 이 정확히 이 이유로 stopImmediatePropagation한다).
          e.stopImmediatePropagation();
          selectMentionCandidate(selectedIdx);
        }
        break;

      case "Escape":
        e.preventDefault();
        close();
        break;
    }
  });

  // ===== 이벤트: 마우스 클릭 선택(mousedown으로 처리 — blur 방지, 미러 :247-255) =====
  popup.addEventListener("mousedown", (e) => {
    e.preventDefault();
    const item = e.target instanceof Element ? e.target.closest<HTMLElement>(".mention-ac-item") : null;
    if (item?.dataset.idx) {
      selectMentionCandidate(parseInt(item.dataset.idx, 10));
    }
  });

  // ===== 이벤트: 외부 클릭 닫기 =====
  document.addEventListener("mousedown", (e) => {
    if (!isOpen) return;
    if (e.target instanceof Node && !popup.contains(e.target) && e.target !== input) {
      close();
    }
  });
})();
